import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { getTagsForObject, updateTags } from "../../api/tags";
import {
	deleteTagHistory,
	getTagHistory,
	TagHistory,
} from "../../api/tags/tag_history";
import { formatDateTime } from "../../utils/date-formatting";

interface TagDebugDialogProps {
	open: boolean;
	onClose: () => void;
	objectName: string;
	objectId: number;
}

export const TagDebugDialog = ({
	open,
	onClose,
	objectName,
	objectId,
}: TagDebugDialogProps) => {
	const [selectedTab, setSelectedTab] = useState(0);
	const [tags, setTags] = useState<string[]>([]);
	const [tagHistory, setTagHistory] = useState<TagHistory[]>([]);
	const [loading, setLoading] = useState(true);

	const refetch = () => {
		setLoading(true);
		Promise.all([
			getTagsForObject(objectName, objectId),
			getTagHistory(objectName, objectId),
		])
			.then(([tagsResult, historyResult]) => {
				setTags(tagsResult);
				setTagHistory(historyResult);
			})
			.catch((err) => console.error("Failed to load tag data:", err))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		if (open) {
			refetch();
		}
	}, [open, objectName, objectId]);

	const handleDeleteTag = async (tagName: string) => {
		const newTags = tags.filter((t) => t !== tagName);
		await updateTags(objectName, objectId, newTags, true);
		refetch();
	};

	const handleDeleteHistory = async (historyId: number) => {
		await deleteTagHistory(historyId);
		refetch();
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			data-component="tag-debug-dialog"
		>
			<DialogTitle data-component="tag-debug-title">
				Debug: Tags for {objectName}/{objectId}
			</DialogTitle>
			<DialogContent data-component="tag-debug-content">
				{loading ? (
					<Typography>Loading...</Typography>
				) : (
					<>
						<Tabs
							value={selectedTab}
							onChange={(_, v) => setSelectedTab(v)}
							sx={{ mb: 2 }}
							data-component="tag-debug-tabs"
						>
							<Tab
								label={`Current Tags (${tags.length})`}
								data-component="tab-current"
							/>
							<Tab
								label={`History (${tagHistory.length})`}
								data-component="tab-history"
							/>
							<Tab label="Raw JSON" data-component="tab-json" />
						</Tabs>

						{selectedTab === 0 && (
							<CurrentTagsTab
								tags={tags}
								tagHistory={tagHistory}
								onDeleteTag={handleDeleteTag}
							/>
						)}
						{selectedTab === 1 && (
							<HistoryTab
								tagHistory={tagHistory}
								onDeleteHistory={handleDeleteHistory}
							/>
						)}
						{selectedTab === 2 && (
							<JsonTab tags={tags} tagHistory={tagHistory} />
						)}
					</>
				)}
			</DialogContent>
			<DialogActions data-component="tag-debug-actions">
				<Button onClick={onClose} data-component="close-button">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const CurrentTagsTab = ({
	tags,
	tagHistory,
	onDeleteTag,
}: {
	tags: string[];
	tagHistory: TagHistory[];
	onDeleteTag: (tagName: string) => void;
}) => {
	// Build a map of tag name -> most recent "added" history entry
	const tagAddedInfo = new Map<string, TagHistory>();
	for (const entry of tagHistory) {
		if (entry.action === "added") {
			const existing = tagAddedInfo.get(entry.name);
			if (!existing || new Date(entry.timestamp) > new Date(existing.timestamp)) {
				tagAddedInfo.set(entry.name, entry);
			}
		}
	}

	return (
		<Stack spacing={2} data-component="current-tags-tab">
			<Typography variant="subtitle2">
				Current tags on this {tags.length > 0 ? "object" : "object (none)"}:
			</Typography>

			{tags.length === 0 ? (
				<Typography color="text.secondary">No tags assigned</Typography>
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Tag Name</TableCell>
								<TableCell>Added By</TableCell>
								<TableCell>Added At</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{tags.map((tag) => {
								const addedInfo = tagAddedInfo.get(tag);
								return (
									<TableRow key={tag}>
										<TableCell>
											<Chip
												label={tag}
												size="small"
												color="primary"
												data-component="tag-chip"
											/>
										</TableCell>
										<TableCell>
											{addedInfo?.username || <em>unknown</em>}
										</TableCell>
										<TableCell>
											{addedInfo ? (
												formatDateTime(addedInfo.timestamp)
											) : (
												<em>unknown</em>
											)}
										</TableCell>
										<TableCell>
											<IconButton
												size="small"
												color="error"
												onClick={() => onDeleteTag(tag)}
												data-component="delete-tag-button"
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Stack>
	);
};

const HistoryTab = ({
	tagHistory,
	onDeleteHistory,
}: {
	tagHistory: TagHistory[];
	onDeleteHistory: (historyId: number) => void;
}) => {
	return (
		<Stack spacing={2} data-component="history-tab">
			<Typography variant="subtitle2">
				Tag audit history ({tagHistory.length} entries):
			</Typography>

			{tagHistory.length === 0 ? (
				<Typography color="text.secondary">No tag history found</Typography>
			) : (
				<TableContainer
					component={Paper}
					variant="outlined"
					sx={{ maxHeight: 500 }}
				>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>ID</TableCell>
								<TableCell>Action</TableCell>
								<TableCell>Tag Name</TableCell>
								<TableCell>By User</TableCell>
								<TableCell>Timestamp</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{tagHistory.map((entry) => (
								<TableRow key={entry.id}>
									<TableCell>{entry.id}</TableCell>
									<TableCell>
										<Stack
											direction="row"
											alignItems="center"
											spacing={0.5}
										>
											{entry.action === "added" ? (
												<AddCircleIcon
													fontSize="small"
													sx={{ color: "success.main" }}
												/>
											) : (
												<RemoveCircleIcon
													fontSize="small"
													sx={{ color: "error.main" }}
												/>
											)}
											<Typography
												variant="body2"
												color={
													entry.action === "added"
														? "success.main"
														: "error.main"
												}
											>
												{entry.action}
											</Typography>
										</Stack>
									</TableCell>
									<TableCell>
										<Chip
											label={entry.name}
											size="small"
											color={
												entry.action === "added"
													? "success"
													: "error"
											}
											variant="outlined"
											data-component="history-tag-chip"
										/>
									</TableCell>
									<TableCell>{entry.username}</TableCell>
									<TableCell>
										{formatDateTime(entry.timestamp)}
									</TableCell>
									<TableCell>
										<IconButton
											size="small"
											color="error"
											onClick={() => onDeleteHistory(entry.id)}
											data-component="delete-history-button"
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Stack>
	);
};

const JsonTab = ({
	tags,
	tagHistory,
}: {
	tags: string[];
	tagHistory: TagHistory[];
}) => {
	return (
		<Stack spacing={2} data-component="json-tab">
			<Box>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Current Tags (raw):
				</Typography>
				<Box
					sx={{
						p: 1,
						border: 1,
						borderColor: "divider",
						maxHeight: 200,
						overflow: "auto",
						whiteSpace: "pre-wrap",
						fontFamily: "monospace",
						fontSize: 12,
					}}
				>
					{JSON.stringify(tags, null, 2)}
				</Box>
			</Box>

			<Box>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Tag History (raw):
				</Typography>
				<Box
					sx={{
						p: 1,
						border: 1,
						borderColor: "divider",
						maxHeight: 400,
						overflow: "auto",
						whiteSpace: "pre-wrap",
						fontFamily: "monospace",
						fontSize: 12,
					}}
				>
					{JSON.stringify(tagHistory, null, 2)}
				</Box>
			</Box>
		</Stack>
	);
};
